import {I18nProvider} from '@react-aria/i18n';
import {setContext} from '@sentry/react';
import React, {use, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Announcer,
    FullscreenSpinner,
    NotificationToast,
} from '@pexip/components';
import {getUserAgentDetails} from '@pexip/media-components';

import {TestId} from '../test/testIds';

import {BrandingContext} from './branding/Context';
import type {Manifest} from './branding/types';
import {AppContextProvider} from './contexts/AppContextProvider';
import {CallHistoryProvider} from './contexts/CallHistoryProvider';
import {ImageStoreContextProvider} from './contexts/ImageStoreContextProvider';
import {InfinityContextProvider} from './contexts/InfinityContextProvider';
import {RegistrationProvider} from './contexts/RegistrationProvider';
import {TouchDeviceProvider} from './contexts/TouchDeviceProvider';
import {UserMediaContextProvider} from './contexts/UserMediaContextProvider';
import {useMainRoutes} from './hooks/useMainRoutes';
import {getShouldRenderCustomStepPageHook} from './hooks/useShouldRenderCustomStepPage';
import {getShouldRenderMobileGetStartedPageHook} from './hooks/useShouldRenderMobileGetStartedPage';
import {CustomStepPage} from './pages/CustomStep.page';
import {MobileGetStarted} from './pages/MobileGetStarted/MobileGetStarted.page';
import {PluginManager} from './plugins';
import {PluginForms} from './plugins/components/Form/Forms';
import {PluginPrompts} from './plugins/components/Prompts';
import {useRouter} from './router';
import type {Infinity} from './services/InfinityClient.service';
import {
    mobileGetStartedNextSignal,
    renderCustomStepSignal,
} from './signals/MeetingFlow.signals';
import {FavoritesLimitReachedModal} from './viewModels/FavoritesLimitReachedModal.viewModel';
import {IncomingCall} from './viewModels/IncomingCall.viewModel';
import {Registration} from './viewModels/Registration.viewModel';

const useShouldRenderMobileGetStartedPage =
    getShouldRenderMobileGetStartedPageHook(mobileGetStartedNextSignal);

export const userAgentDetails = getUserAgentDetails(navigator.userAgent);

/**
 * Root view of Infinity Connect. Suspends until `branding` resolves, so it
 * must be rendered inside a `Suspense` boundary.
 *
 * @example
 * const branding = bootstrapBranding();
 * <Suspense fallback={<FullscreenSpinner />}>
 *     <App infinity={infinity} branding={branding} />
 * </Suspense>
 */
export const App: React.FC<{
    infinity: Infinity;
    /** Branding that is applied before the promise resolves. */
    branding: Promise<Manifest>;
}> = ({infinity, branding}) => {
    useRouter();
    const brand = use(branding);
    const {i18n} = useTranslation();

    const matchedRoutes = useMainRoutes();
    const is404 = matchedRoutes.some(route => route.props.match.fallback);
    const shouldRenderMobileGetStartedPage =
        useShouldRenderMobileGetStartedPage();

    const useShouldRenderCustomStepPage = getShouldRenderCustomStepPageHook(
        renderCustomStepSignal,
        brand.customStepConfig,
    );
    const shouldRenderCustomStepPage = useShouldRenderCustomStepPage();

    useEffect(() => {
        if (brand.plugins) {
            setContext('Plugins', {
                ...brand.plugins.map(plugin => ({
                    [plugin.src]: {},
                })),
            });
        }
    }, [brand]);

    useEffect(() => {
        document.documentElement.lang = i18n.language;
        document.documentElement.dir = i18n.dir(i18n.language);
    }, [i18n.language, i18n.dir]);

    const renderPage = () => {
        if (!is404 && shouldRenderMobileGetStartedPage) {
            return <MobileGetStarted />;
        }

        if (!is404 && shouldRenderCustomStepPage) {
            return <CustomStepPage />;
        }

        return matchedRoutes;
    };

    /**
     * Since useModalOverlay from \@react-aria/overlays is used in useModal hook to add aria-hidden="true" to all DOM elements
     * that are not part of a modal, the data-live-announcer attribute is used to exclude the announcer div
     * from that logic.
     * This also protects the Announcer from AriaMenu aria-hidden="true" logic.
     */
    return (
        <React.Suspense fallback={<FullscreenSpinner />}>
            <I18nProvider locale={i18n.language}>
                <main>
                    <Announcer
                        data-live-announcer="true"
                        testId={TestId.Announcer}
                    />
                    <BrandingContext.Provider value={brand}>
                        <ImageStoreContextProvider>
                            <CallHistoryProvider>
                                <InfinityContextProvider infinity={infinity}>
                                    <UserMediaContextProvider>
                                        <AppContextProvider>
                                            <TouchDeviceProvider>
                                                <RegistrationProvider>
                                                    <PluginManager>
                                                        <h1 hidden>
                                                            {document.title}
                                                        </h1>
                                                        <React.Suspense
                                                            fallback={
                                                                <FullscreenSpinner />
                                                            }
                                                        >
                                                            {renderPage()}
                                                        </React.Suspense>
                                                        <PluginForms />
                                                        <PluginPrompts />
                                                        <Registration />
                                                        <IncomingCall />
                                                        <FavoritesLimitReachedModal />
                                                        <NotificationToast />
                                                    </PluginManager>
                                                </RegistrationProvider>
                                            </TouchDeviceProvider>
                                        </AppContextProvider>
                                    </UserMediaContextProvider>
                                </InfinityContextProvider>
                            </CallHistoryProvider>
                        </ImageStoreContextProvider>
                    </BrandingContext.Provider>
                </main>
            </I18nProvider>
        </React.Suspense>
    );
};
